import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { NotificationService } from '@shared/services/notification.service';
import {
  CreateOrganizationUnitEventTypeDto,
  OrganizationUnitEventTypeDto,
  OrganizationUnitEventTypeManagementService,
  UpdateOrganizationUnitEventTypeDto,
} from '../../services/organization-unit-event-type-management.service';

@Component({
  selector: 'app-organization-unit-event-type-editor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './organization-unit-event-type-editor.component.html',
  styleUrl: './organization-unit-event-type-editor.component.scss',
})
export class OrganizationUnitEventTypeEditorComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  typeId: number | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly service: OrganizationUnitEventTypeManagementService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly notificationService: NotificationService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      description: ['', [Validators.maxLength(150)]],
    });
  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params['id'] && params['id'] !== 'new') {
          this.typeId = +params['id'];
          this.isEditMode = true;
          this.loadType();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  saveType(): void {
    if (!this.form.valid) {
      this.notificationService.warning('Por favor, completa todos los campos correctamente');
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();

    const formValue = this.form.value;

    if (this.isEditMode && this.typeId) {
      this.updateType(this.typeId, formValue);
    } else {
      this.createType(formValue);
    }
  }

  cancel(): void {
    this.router.navigate(['/configuration/community-event-types']);
  }

  getControl(name: string) {
    return this.form.get(name);
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Editar tipo de evento' : 'Nuevo tipo de evento';
  }

  private loadType(): void {
    if (!this.typeId) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.service.getById(this.typeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (type: OrganizationUnitEventTypeDto) => {
          this.form.patchValue({
            name: type.name,
            description: type.description,
          });
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.notificationService.error('Error al cargar el tipo de evento');
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private createType(dto: CreateOrganizationUnitEventTypeDto): void {
    this.service.create(dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Tipo de evento creado exitosamente');
          this.router.navigate(['/configuration/community-event-types']);
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          this.notificationService.error(error?.error?.message || error?.error?.title || 'Error al crear el tipo de evento');
          this.isSaving = false;
          this.cdr.markForCheck();
        },
      });
  }

  private updateType(id: number, dto: UpdateOrganizationUnitEventTypeDto): void {
    this.service.update(id, dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Tipo de evento actualizado exitosamente');
          this.router.navigate(['/configuration/community-event-types']);
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          this.notificationService.error(error?.error?.message || error?.error?.title || 'Error al actualizar el tipo de evento');
          this.isSaving = false;
          this.cdr.markForCheck();
        },
      });
  }
}
